import React, { useState, useEffect } from "react";
import { HawanStep, Mantra } from "../types";
import { HAWAN_STEPS, VEDIC_MANTRAS } from "../data";
import { 
  Flame, 
  BookOpen, 
  Bookmark, 
  BookmarkCheck, 
  Trash2, 
  Globe, 
  ChevronDown, 
  ChevronUp, 
  Sparkles, 
  Info,
  Maximize2,
  Minimize2,
  CheckCircle2
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { createAudioContext } from "../lib/audio-utils";

interface HawanGuideProps {
  onMantraSelect: (mantra: Mantra) => void;
  activeMantraId: string;
}

interface BookmarkItem {
  tradition: string;
  stepNumber: number;
  title: string;
  sanskrit: string;
}

export default function HawanGuide({ onMantraSelect, activeMantraId }: HawanGuideProps) {
  const [activeTradition, setActiveTradition] = useState<"vedic" | "avestan" | "roman" | "norse">("vedic");
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [offerCount, setOfferCount] = useState(0);
  const [showOfferAnimation, setShowOfferAnimation] = useState(false);
  const [expandedStepIds, setExpandedStepIds] = useState<string[]>([]);

  // Bookmarks state persisted in localStorage
  const [bookmarks, setBookmarks] = useState<BookmarkItem[]>(() => {
    try {
      const saved = localStorage.getItem("vedic_hawan_bookmarks");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Filter steps by active tradition
  const steps = HAWAN_STEPS.filter(s => s.tradition === activeTradition);
  const activeStep = steps[currentStepIndex] || steps[0];

  useEffect(() => {
    setCurrentStepIndex(0);
    setTimeLeft(120);
    setIsTimerActive(false);
    const firstStep = HAWAN_STEPS.find(s => s.tradition === activeTradition);
    if (firstStep) {
      setExpandedStepIds([firstStep.id]);
    } else {
      setExpandedStepIds([]);
    }
  }, [activeTradition]);

  const isStepBookmarked = (step: HawanStep) => {
    return bookmarks.some(
      b => b.tradition === step.tradition && b.stepNumber === step.stepNumber
    );
  };

  const toggleStepBookmark = (step: HawanStep) => {
    let updated: BookmarkItem[];
    if (isStepBookmarked(step)) {
      updated = bookmarks.filter(
        b => !(b.tradition === step.tradition && b.stepNumber === step.stepNumber)
      );
    } else {
      updated = [
        ...bookmarks,
        {
          tradition: step.tradition,
          stepNumber: step.stepNumber,
          title: step.title,
          sanskrit: step.sanskrit
        }
      ];
    }
    setBookmarks(updated);
    try {
      localStorage.setItem("vedic_hawan_bookmarks", JSON.stringify(updated));
    } catch {}
  };

  const removeBookmark = (item: BookmarkItem) => {
    const updated = bookmarks.filter(
      b => !(b.tradition === item.tradition && b.stepNumber === item.stepNumber)
    );
    setBookmarks(updated);
    try {
      localStorage.setItem("vedic_hawan_bookmarks", JSON.stringify(updated));
    } catch {}
  };

  const clearAllBookmarks = () => {
    setBookmarks([]);
    try {
      localStorage.removeItem("vedic_hawan_bookmarks");
    } catch {}
  };

  const jumpToBookmark = (b: BookmarkItem) => {
    if (b.tradition !== activeTradition) {
      setActiveTradition(b.tradition as any);
    }
    const targetSteps = HAWAN_STEPS.filter(s => s.tradition === b.tradition);
    const idx = targetSteps.findIndex(s => s.stepNumber === b.stepNumber);
    if (idx !== -1) {
      setCurrentStepIndex(idx);
      const targetStep = targetSteps[idx];
      if (targetStep && !expandedStepIds.includes(targetStep.id)) {
        setExpandedStepIds(prev => [...prev, targetStep.id]);
      }
    }
    setTimeLeft(120);
    setIsTimerActive(false);
  };

  const handleTraditionChange = (trad: "vedic" | "avestan" | "roman" | "norse") => {
    setActiveTradition(trad);
  };

  const toggleStepExpansion = (stepId: string, idx: number) => {
    setCurrentStepIndex(idx);
    setExpandedStepIds(prev =>
      prev.includes(stepId) ? prev.filter(id => id !== stepId) : [...prev, stepId]
    );
  };

  const expandAllSteps = () => {
    setExpandedStepIds(steps.map(s => s.id));
  };

  const collapseAllSteps = () => {
    setExpandedStepIds([]);
  };

  // Material checklists based on tradition
  const checklists: Record<string, string[]> = {
    vedic: [
      "Concentric Copper Hawan Kunda (Fire pit)",
      "Pure Cow Ghee (Clarified butter)",
      "Sruva & Sruk (Wooden pouring spoons)",
      "Samidha (Dry mango wood twigs)",
      "Havan Samagri (Sacred dried herbs & seeds)",
      "Camphor & Matchbox for ignition"
    ],
    avestan: [
      "Afrinagan Urn (Metallic chalice / Fire vase)",
      "Dry clean sandalwood twigs (Aesma)",
      "Loban (Pure frankincense powder)",
      "Fresh spring water for hand cleansing",
      "Kusti (Sacred thread / sash)"
    ],
    roman: [
      "Bronze Tripod Altar or Hearth plate",
      "Mola Salsa (Spelt flour mixed with salt)",
      "Patera (Flat shallow libation dish)",
      "Olive twigs & Laurel dry leaves",
      "Pure honey & olive oil"
    ],
    norse: [
      "Circular Raw Stone Hearth pit",
      "Dried juniper berries & pine needles",
      "Oak or ash wood logs",
      "Ceremonial steel fire steel / flint",
      "Water or mead for consecration horn"
    ]
  };

  // Timer loop
  useEffect(() => {
    let interval: any = null;
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
      playRitualChime();
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeLeft]);

  // Synthesize a bell chime upon completing a step or offering
  const playRitualChime = () => {
    try {
      const ctx = createAudioContext();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(587.33, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(293.66, ctx.currentTime + 1.5);

      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 2.5);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 2.5);
    } catch (e) {
      console.warn("Web Audio chime failed:", e);
    }
  };

  // Triggered when offering is made
  const triggerOffering = (stepIndex?: number) => {
    if (typeof stepIndex === "number") {
      setCurrentStepIndex(stepIndex);
    }
    setOfferCount(prev => prev + 1);
    setShowOfferAnimation(true);
    playRitualChime();

    try {
      const ctx = createAudioContext();
      if (!ctx) return;
      const bufferSize = ctx.sampleRate * 0.4;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i/bufferSize, 3);
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      noise.connect(gain);
      gain.connect(ctx.destination);
      noise.start();
    } catch(e){}

    setTimeout(() => {
      setShowOfferAnimation(false);
    }, 1000);
  };

  // Load step mantra into oscilloscope
  const selectStepMantra = (step: HawanStep) => {
    if (step.targetMantraId) {
      const matching = VEDIC_MANTRAS.find(m => m.id === step.targetMantraId);
      if (matching) {
        onMantraSelect(matching);
      }
    } else {
      const defaultMantra = VEDIC_MANTRAS.find(m => m.tradition === activeTradition);
      if (defaultMantra) onMantraSelect(defaultMantra);
    }
  };

  const getCategoryBadgeClass = (category: HawanStep["category"]) => {
    switch (category) {
      case "preliminary":
        return "bg-sky-500/10 text-sky-400 border-sky-500/30";
      case "invocation":
        return "bg-amber-500/10 text-amber-400 border-amber-500/30";
      case "offering":
        return "bg-orange-500/10 text-orange-400 border-orange-500/30";
      case "concluding":
        return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
      default:
        return "bg-slate-800 text-slate-300 border-slate-700";
    }
  };

  return (
    <div className="space-y-6 text-left">
      
      {/* Tradition Selector Bar */}
      <div className="flex flex-wrap gap-2 bg-slate-900/30 p-2 border border-slate-900/80 rounded-2xl">
        {(["vedic", "avestan", "roman", "norse"] as const).map(trad => {
          const isActive = activeTradition === trad;
          const titles = {
            vedic: "Vedic Sanskrit",
            avestan: "Zoroastrian Avestan",
            roman: "Greco-Roman Latin",
            norse: "Norse Runic"
          };
          return (
            <button
              key={trad}
              onClick={() => handleTraditionChange(trad)}
              className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold tracking-wider font-mono cursor-pointer transition uppercase text-center ${
                isActive
                  ? "bg-cyan-600 text-slate-950 shadow-md shadow-cyan-500/10 border border-cyan-400/20"
                  : "bg-slate-950/60 text-slate-400 hover:text-white border border-slate-900"
              }`}
            >
              {titles[trad]}
            </button>
          );
        })}
      </div>

      {/* Saved Ritual Bookmarks Bar */}
      {bookmarks.length > 0 && (
        <div className="bg-slate-950/90 border border-amber-500/30 rounded-2xl p-4 flex flex-col gap-2.5 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bookmark className="w-3.5 h-3.5 text-amber-400 fill-amber-400/20" />
              <span className="text-[11px] font-mono font-bold text-amber-300 uppercase tracking-wider">
                Saved Ritual Bookmarks ({bookmarks.length})
              </span>
            </div>
            <button
              onClick={clearAllBookmarks}
              className="text-[10px] font-mono text-slate-500 hover:text-red-400 transition cursor-pointer"
            >
              Clear All
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {bookmarks.map((b, i) => {
              const isCurrent = b.tradition === activeStep?.tradition && b.stepNumber === activeStep?.stepNumber;
              return (
                <div
                  key={i}
                  onClick={() => jumpToBookmark(b)}
                  className={`group flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs transition cursor-pointer border ${
                    isCurrent
                      ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
                      : "bg-slate-900/80 border-slate-800 text-slate-300 hover:bg-slate-800 hover:border-slate-700"
                  }`}
                >
                  <span className="text-[10px] font-mono text-amber-400/80 font-bold uppercase">
                    [{b.tradition.slice(0, 3)}] Step {b.stepNumber}
                  </span>
                  <span className="truncate max-w-[140px] font-medium text-[11px]">{b.title}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBookmark(b);
                    }}
                    title="Remove bookmark"
                    className="opacity-60 hover:opacity-100 text-slate-400 hover:text-red-400 transition"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* Procedural Altar (5 Columns - Sticky on Desktop) */}
        <div className="lg:col-span-5 lg:sticky lg:top-6 bg-slate-950/80 border border-slate-900 rounded-3xl p-6 flex flex-col justify-between items-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-grid-pattern opacity-5 pointer-events-none" />
          
          <div className="w-full flex justify-between items-center mb-2">
            <span className="text-[10px] text-cyan-400 font-mono tracking-widest uppercase block">
              // SACRED EMBODIED VISUALIZER
            </span>
            <div className="bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1 text-[10px] font-mono text-slate-400">
              Offerings: <span className="text-cyan-400 font-bold">{offerCount}</span>
            </div>
          </div>

          <div className="w-full text-center my-2 py-1.5 px-3 bg-slate-900/60 border border-slate-800/80 rounded-xl">
            <span className="text-[11px] font-mono text-amber-300/90 font-semibold block">
              Focused: Step {activeStep?.stepNumber} — {activeStep?.title}
            </span>
          </div>

          {/* Procedural SVG Altars */}
          <div className="relative w-60 h-60 flex items-center justify-center my-4">
            
            {/* Pulsing Aura */}
            <div className="absolute inset-0 bg-orange-500/5 blur-3xl rounded-full animate-pulse pointer-events-none" />

            {/* Vedic Yajnic Altar */}
            {activeTradition === "vedic" && (
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <rect x="10" y="10" width="80" height="80" fill="none" stroke="#eab308" strokeWidth="2.5" strokeDasharray="3 3" />
                <rect x="20" y="20" width="60" height="60" fill="none" stroke="#ea580c" strokeWidth="2" />
                <rect x="30" y="30" width="40" height="40" fill="#78350f" stroke="#f97316" strokeWidth="1.5" />
                <circle cx="50" cy="50" r="14" fill="#451a03" />
              </svg>
            )}

            {/* Avestan Urn */}
            {activeTradition === "avestan" && (
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <path d="M 30,90 L 70,90 L 60,70 L 40,70 Z" fill="#475569" stroke="#94a3b8" strokeWidth="1" />
                <rect x="46" y="50" width="8" height="20" fill="#64748b" stroke="#cbd5e1" strokeWidth="1" />
                <path d="M 25,30 L 75,30 L 65,52 L 35,52 Z" fill="#334155" stroke="#94a3b8" strokeWidth="1.5" />
                <ellipse cx="50" cy="30" rx="25" ry="5" fill="#1e293b" stroke="#cbd5e1" strokeWidth="1" />
              </svg>
            )}

            {/* Roman Tripod */}
            {activeTradition === "roman" && (
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <line x1="30" y1="35" x2="15" y2="90" stroke="#a1a1aa" strokeWidth="2.5" />
                <line x1="70" y1="35" x2="85" y2="90" stroke="#a1a1aa" strokeWidth="2.5" />
                <line x1="50" y1="35" x2="50" y2="95" stroke="#71717a" strokeWidth="2" />
                <ellipse cx="50" cy="35" rx="30" ry="10" fill="#27272a" stroke="#d4d4d8" strokeWidth="2" />
                <ellipse cx="50" cy="35" rx="25" ry="7" fill="#18181b" />
              </svg>
            )}

            {/* Norse Stone Pit */}
            {activeTradition === "norse" && (
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <circle cx="50" cy="50" r="38" fill="none" stroke="#52525b" strokeWidth="6" />
                <circle cx="50" cy="50" r="30" fill="none" stroke="#3f3f46" strokeWidth="4" />
                <text x="50" y="24" fill="#a1a1aa" fontSize="6" textAnchor="middle" fontFamily="monospace">ᚠ</text>
                <text x="76" y="52" fill="#a1a1aa" fontSize="6" textAnchor="middle" fontFamily="monospace">ᚱ</text>
                <text x="50" y="82" fill="#a1a1aa" fontSize="6" textAnchor="middle" fontFamily="monospace">ᛋ</text>
                <text x="24" y="52" fill="#a1a1aa" fontSize="6" textAnchor="middle" fontFamily="monospace">ᚦ</text>
              </svg>
            )}

            {/* Dynamic Flame Layer */}
            <div className="absolute flex justify-center items-end bottom-[35%] w-24 h-24">
              <motion.div
                animate={{
                  scaleY: [1, 1.15, 0.95, 1.1, 1],
                  scaleX: [1, 0.9, 1.1, 0.95, 1],
                  rotate: [0, -1, 2, -2, 0]
                }}
                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                className="w-12 h-16 bg-gradient-to-t from-orange-600 via-yellow-500 to-amber-200 rounded-b-full rounded-t-[70%] origin-bottom shadow-lg shadow-orange-500/30 filter blur-[0.5px]"
              />
              
              <motion.div
                animate={{
                  scaleY: [1, 1.1, 0.9, 1.05, 1],
                  scaleX: [1, 0.95, 1.05, 0.9, 1]
                }}
                transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                className="absolute w-6 h-10 bg-cyan-200 rounded-b-full rounded-t-[70%] origin-bottom filter blur-[1px] opacity-70 mb-1"
              />
            </div>

            {/* Pour Offering Splash Animation */}
            <AnimatePresence>
              {showOfferAnimation && (
                <motion.div
                  initial={{ scale: 0, opacity: 1 }}
                  animate={{ scale: 1.5, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  className="absolute inset-0 border-2 border-orange-400 rounded-full bg-orange-500/10 pointer-events-none"
                />
              )}
            </AnimatePresence>

          </div>

          <button
            onClick={() => triggerOffering(currentStepIndex)}
            className="w-full mt-2 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-slate-950 font-mono font-bold uppercase tracking-widest text-xs py-3 px-5 rounded-xl cursor-pointer transition shadow-md shadow-orange-500/10 flex items-center justify-center gap-2"
          >
            <Flame className="h-4 w-4" />
            <span>Pour Ritual Offering</span>
          </button>

          {/* Rites Material Checklist */}
          <div className="w-full mt-6 pt-4 border-t border-slate-900">
            <div className="flex justify-between items-center mb-3">
              <span className="text-[10px] text-cyan-400 font-mono tracking-widest uppercase block">
                // LITURGICAL MATERIAL SYNAPSE
              </span>
              <span className="text-[10px] font-mono text-slate-500 uppercase">
                {activeTradition} Altar
              </span>
            </div>

            <div className="space-y-2">
              {checklists[activeTradition]?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2.5 p-2 bg-slate-900/40 border border-slate-900/80 rounded-xl">
                  <div className="h-1.5 w-1.5 rounded-full bg-cyan-500/80 shrink-0" />
                  <span className="text-[11px] text-slate-300 font-light leading-normal">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Collapsible Steps Accordion List (7 Columns) */}
        <div className="lg:col-span-7 flex flex-col gap-4">
          
          <div className="flex items-center justify-between bg-slate-950/80 border border-slate-900 rounded-2xl px-5 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <h3 className="text-xs font-mono font-bold text-slate-200 uppercase tracking-wider">
                {activeTradition.toUpperCase()} Hawan Liturgical Sequence ({steps.length} Steps)
              </h3>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={expandAllSteps}
                className="flex items-center gap-1 text-[10px] font-mono text-slate-400 hover:text-cyan-400 bg-slate-900/80 hover:bg-slate-900 border border-slate-800 px-2.5 py-1 rounded-lg transition cursor-pointer"
              >
                <Maximize2 className="w-3 h-3" />
                <span>Expand All</span>
              </button>
              <button
                onClick={collapseAllSteps}
                className="flex items-center gap-1 text-[10px] font-mono text-slate-400 hover:text-cyan-400 bg-slate-900/80 hover:bg-slate-900 border border-slate-800 px-2.5 py-1 rounded-lg transition cursor-pointer"
              >
                <Minimize2 className="w-3 h-3" />
                <span>Collapse All</span>
              </button>
            </div>
          </div>

          {/* List of Collapsible Step Cards */}
          <div className="space-y-3">
            {steps.map((step, idx) => {
              const isExpanded = expandedStepIds.includes(step.id);
              const isActive = currentStepIndex === idx;
              const bookmarked = isStepBookmarked(step);

              return (
                <div
                  key={step.id}
                  className={`bg-slate-950/80 rounded-2xl border transition-all duration-200 overflow-hidden ${
                    isActive
                      ? "border-cyan-500/50 shadow-md shadow-cyan-500/5"
                      : "border-slate-900 hover:border-slate-800"
                  }`}
                >
                  {/* Collapsible Step Header */}
                  <div
                    onClick={() => toggleStepExpansion(step.id, idx)}
                    className="p-4 flex items-center justify-between gap-3 cursor-pointer select-none hover:bg-slate-900/40 transition"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-8 h-8 rounded-xl font-mono text-xs font-bold flex items-center justify-center shrink-0 border ${
                          isActive
                            ? "bg-cyan-500/20 text-cyan-300 border-cyan-400/40"
                            : "bg-slate-900 text-slate-400 border-slate-800"
                        }`}
                      >
                        {step.stepNumber}
                      </div>

                      <div className="min-w-0 space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-slate-200 font-mono">
                            {step.title}
                          </span>
                          <span
                            className={`text-[9px] font-mono uppercase font-semibold px-2 py-0.5 rounded-md border ${getCategoryBadgeClass(
                              step.category
                            )}`}
                          >
                            {step.category}
                          </span>
                        </div>
                        <p className="text-xs text-amber-300/80 font-medium truncate font-devanagari">
                          {step.sanskrit}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStepBookmark(step);
                        }}
                        title={bookmarked ? "Remove Bookmark" : "Bookmark Step"}
                        className={`p-1.5 rounded-lg text-xs transition cursor-pointer border ${
                          bookmarked
                            ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                            : "bg-slate-900 text-slate-500 hover:text-slate-300 border-slate-800"
                        }`}
                      >
                        {bookmarked ? (
                          <BookmarkCheck className="h-3.5 w-3.5 text-amber-400 fill-amber-400/20" />
                        ) : (
                          <Bookmark className="h-3.5 w-3.5" />
                        )}
                      </button>

                      <div
                        className={`p-1.5 rounded-lg text-slate-400 transition ${
                          isExpanded ? "bg-cyan-500/10 text-cyan-400" : "bg-slate-900"
                        }`}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Step Details */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="overflow-hidden border-t border-slate-900/80 bg-slate-900/20"
                      >
                        <div className="p-5 space-y-4">
                          
                          {/* Native Scripture & Transliteration Box */}
                          <div className="bg-slate-950/90 border border-slate-800/80 rounded-xl p-4 space-y-2">
                            <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase tracking-wider block">
                              // SACRED TEXT & TRANSLITERATION
                            </span>
                            <p className="text-lg font-medium text-amber-100 leading-relaxed font-devanagari">
                              {step.sanskrit}
                            </p>
                            <p className="text-xs font-mono text-cyan-400/90 italic">
                              {step.iast}
                            </p>
                            <div className="pt-2 border-t border-slate-900 text-xs font-serif text-slate-300 italic">
                              "{step.translation}"
                            </div>
                          </div>

                          {/* Pedagogical Details & Sacred Procedure */}
                          <div className="space-y-1.5 bg-slate-900/50 border border-slate-800/60 rounded-xl p-3.5">
                            <div className="flex items-center gap-1.5 text-xs font-mono font-bold text-amber-300 uppercase tracking-wider">
                              <BookOpen className="w-3.5 h-3.5 text-amber-400" />
                              <span>Pedagogical Procedure & Ritual Action</span>
                            </div>
                            <p className="text-xs text-slate-300 leading-relaxed font-light pl-2 border-l-2 border-amber-500/40">
                              {step.procedure}
                            </p>
                          </div>

                          {/* Metaphysical & Philosophical Meaning */}
                          <div className="space-y-1.5 bg-slate-900/50 border border-slate-800/60 rounded-xl p-3.5">
                            <div className="flex items-center gap-1.5 text-xs font-mono font-bold text-cyan-300 uppercase tracking-wider">
                              <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                              <span>Metaphysical & Spiritual Significance</span>
                            </div>
                            <p className="text-xs text-slate-400 leading-relaxed italic font-light pl-2 border-l-2 border-cyan-500/40">
                              {step.spiritualMeaning}
                            </p>
                          </div>

                          {/* Action Toolbar for this Step */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-900">
                            <button
                              onClick={() => selectStepMantra(step)}
                              className="bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-cyan-400 font-mono text-[10px] font-bold uppercase tracking-wider py-2 px-3 rounded-xl transition cursor-pointer flex items-center gap-1.5"
                            >
                              <BookOpen className="h-3.5 w-3.5" />
                              <span>Load Liturgy to Osc</span>
                            </button>

                            <button
                              onClick={() => triggerOffering(idx)}
                              className="bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/40 text-orange-300 font-mono text-[10px] font-bold uppercase tracking-wider py-2 px-3 rounded-xl transition cursor-pointer flex items-center gap-1.5 ml-auto"
                            >
                              <Flame className="h-3.5 w-3.5 text-orange-400" />
                              <span>Pour Step Offering</span>
                            </button>
                          </div>

                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

        </div>

      </div>
    </div>
  );
}

